import { CSSProperties, MutableRefObject, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { InputAutocompleteOption } from '../input-autocomplete-option/input-autocomplete-option';
import { ActionLabeledInputButton, InputButton } from '../text-field/textField';

export function renderOptionAccordingType<T>({
    list,
    index,
    keyValue,
    keyLabel,
    style,
    classes,
    action,
    multiple,
    selected,
    parentName,
    disabled = false,
}: {
    list: T[];
    index: number;
    keyValue?: string;
    keyLabel?: string;
    style?: CSSProperties;
    classes?: string;
    action?(value: string): void;
    multiple?: boolean;
    selected?: string[];
    parentName?: string;
    disabled?: boolean;
}): ReactNode {
    const isSelected = isSelectedAccordingType({
        item: list[index as number],
        keyLabel,
        keyValue,
        selectedList: selected as string[],
    });

    if (keyValue && keyLabel) {
        return (
            <InputAutocompleteOption
                className={twMerge(isSelected && 'bg-primary-50', classes)}
                value={(list[index] as unknown as Record<string, string>)[keyValue] as unknown as string}
                label={(list[index] as unknown as Record<string, string>)[keyLabel] as unknown as string}
                style={style}
                action={action}
                multiple={multiple}
                selected={isSelected}
                dataTestId={parentName ? `${parentName}-combo-option` : undefined}
                disabled={disabled}
            />
        );
    } else if (keyValue) {
        return (
            <InputAutocompleteOption
                className={twMerge(isSelected && 'bg-primary-50', classes)}
                value={(list[index] as unknown as Record<string, string>)[keyValue] as unknown as string}
                label={(list[index] as unknown as Record<string, string>)[keyValue] as unknown as string}
                style={style}
                action={action}
                multiple={multiple}
                selected={isSelected}
                dataTestId={parentName ? `${parentName}-combo-option` : undefined}
                disabled={disabled}
            />
        );
    } else {
        return (
            <InputAutocompleteOption
                className={twMerge(isSelected && 'bg-primary-50', classes)}
                value={list[index] as unknown as string}
                label={list[index] as unknown as string}
                style={style}
                action={action}
                multiple={multiple}
                selected={isSelected}
                dataTestId={parentName ? `${parentName}-combo-option` : undefined}
                disabled={disabled}
            />
        );
    }
}

export function renderOptionWithAccessor<T>({
    name,
    item,
    keyValue,
    keyLabel,
    style,
    classes,
    action,
    multiple,
    selected,
    disabled = false,
    hasDeviceField,
    noDeviceMessage,
    tachographIcon,
    measureRef,
    dataIndex,
}: {
    name: string;
    item: T;
    keyValue: string;
    keyLabel: string;
    style?: CSSProperties;
    classes?: string;
    action?(value: string): void;
    multiple?: boolean;
    selected?: string | string[];
    parentName?: string;
    disabled?: boolean;
    hasDeviceField?: string;
    noDeviceMessage?: string;
    tachographIcon?: React.ReactNode | ((option: T) => React.ReactNode);
    /** Virtualizer measure callback ref (TanStack measureElement) — enables dynamic row height */
    measureRef?: (node: Element | null) => void;
    /** Row index for the virtualizer's data-index attribute */
    dataIndex?: number;
}): ReactNode {
    const itemRecord = item as unknown as Record<string, string>;
    const isSelected = Array.isArray(selected)
        ? selected.includes(itemRecord?.[keyLabel])
        : selected === itemRecord?.[keyLabel];
    // Verificar si el dispositivo está reportando

    return (
        <InputAutocompleteOption
            ref={measureRef}
            dataIndex={dataIndex}
            className={twMerge(isSelected && 'bg-primary-50', classes)}
            value={itemRecord?.[keyValue] as unknown as string}
            label={itemRecord?.[keyLabel] as unknown as string}
            style={style}
            action={action}
            multiple={multiple}
            selected={isSelected}
            dataTestId={name ? `${name}-combo-option` : undefined}
            disabled={disabled}
            hasDeviceField={hasDeviceField}
            noDeviceMessage={noDeviceMessage}
            deviceStatus={hasDeviceField ? (item as Record<string, any>)?.[hasDeviceField] : undefined}
            tachographIcon={typeof tachographIcon === 'function' ? tachographIcon(item) : tachographIcon}
        />
    );
}

export const renderOptionSimple = ({
    name,
    item,
    isSelected,
    style,
    classes,
    action,
    disabled = false,
    multiple = false,
}: {
    name: string;
    item: string;
    isSelected: boolean;
    style?: CSSProperties;
    classes?: string;
    action?(value: string): void;
    disabled?: boolean;
    multiple?: boolean;
}) => {
    return (
        <InputAutocompleteOption
            className={twMerge(isSelected && 'bg-primary-50', classes)}
            value={item}
            label={item}
            style={style}
            action={action}
            multiple={multiple}
            selected={isSelected}
            dataTestId={`${name}-combo-option`}
            disabled={disabled}
        />
    );
};

export function findElementAccordingType<T>({
    item,
    keyLabel,
    keyValue,
    compareValue,
}: {
    item: T;
    keyValue?: string;
    keyLabel?: string;
    compareValue: string;
}) {
    if (keyValue && keyLabel) {
        return (item as unknown as Record<string, string>)[keyLabel].toLowerCase().includes(compareValue.toLowerCase());
    } else if (keyValue) {
        return (item as unknown as Record<string, string>)[keyValue].toLowerCase().includes(compareValue.toLowerCase());
    } else {
        return (item as unknown as string).toLowerCase().includes(compareValue.toLowerCase());
    }
}

export function isSelectedAccordingType<T>({
    item,
    keyLabel,
    keyValue,
    selectedList = [],
}: {
    item: T;
    keyValue?: string;
    keyLabel?: string;
    selectedList: string[];
}) {
    if (keyValue && keyLabel) {
        return selectedList.includes((item as unknown as Record<string, string>)[keyLabel]);
    } else if (keyValue) {
        return selectedList.includes((item as unknown as Record<string, string>)[keyValue]);
    } else {
        return selectedList.includes(item as unknown as string);
    }
}

export function filterQuery<T>(options: T[], query: string, keyLabel?: string, keyValue?: string): T[] {
    const mainAux = query.length
        ? options.filter((option) => {
              const aux = findElementAccordingType({
                  item: option,
                  keyLabel,
                  keyValue,
                  compareValue: query,
              });
              return aux;
          })
        : options;
    return mainAux;
}

export function filterQuerySimple(options: string[], query: string): string[] {
    const mainAux = query.length
        ? options.filter((option) => option.toLocaleLowerCase().includes(query.toLocaleLowerCase()))
        : options;
    return mainAux;
}

export const getQueryStateFlags = ({
    query,
    filteredOptionsLength,
    isLoading,
    filterRemote,
    lastCalledQuery,
}: {
    query: string;
    filteredOptionsLength: number;
    isLoading: boolean;
    filterRemote?: boolean;
    lastCalledQuery?: string;
}) => {
    const hasQuery = query.length > 0;
    const queryExistAndThereIsNotOptions = hasQuery && filteredOptionsLength === 0 && !isLoading;
    const queryExistAndAsksRemoteFirst = Boolean(filterRemote) && hasQuery && !isLoading && query !== lastCalledQuery;

    return {
        queryExistAndThereIsNotOptions,
        queryExistAndAsksRemoteFirst,
    };
};

export const handleAutocompleteRemoteQuery = ({
    query,
    filteredOptionsLength,
    isLoading,
    filterRemote,
    calledQueryRef,
    shouldOpenDropdown,
    openDropdown,
    debounced,
}: {
    query: string;
    filteredOptionsLength: number;
    isLoading: boolean;
    filterRemote?: boolean;
    calledQueryRef: MutableRefObject<string>;
    shouldOpenDropdown: boolean;
    openDropdown(): void;
    debounced(value: string): void;
}) => {
    const { queryExistAndThereIsNotOptions, queryExistAndAsksRemoteFirst } = getQueryStateFlags({
        query,
        filteredOptionsLength,
        isLoading,
        filterRemote,
        lastCalledQuery: calledQueryRef.current,
    });

    if (shouldOpenDropdown) {
        openDropdown();
        return;
    }

    if (queryExistAndThereIsNotOptions || queryExistAndAsksRemoteFirst) {
        debounced(query);
        calledQueryRef.current = query;
    }
};

export const getLabelFromValue = <T,>({
    value,
    options,
    enableEmptyOption = false,
    accessorLabel,
    accessorValue,
}: {
    value?: string;
    options: T[];
    enableEmptyOption?: boolean;
    accessorLabel?: string;
    accessorValue?: string;
}) => {
    if ((!value || value === '') && enableEmptyOption) return '';
    // Check if value is in options
    const foundItem = options.find((item) => (item[accessorValue as keyof T] as unknown as string) === value) as T;
    if (!foundItem) {
        return '';
    }
    // Return label of found item
    return foundItem[accessorLabel as keyof T] as unknown as string;
};

export const getStartButtonDefinition = ({ show }: { show: boolean }): InputButton | undefined => {
    return show
        ? {
              background: 'transparent',
              iconName: 'ri-search-line',
          }
        : undefined;
};

export const getEndButtonDefinition = ({
    isLoading,
    show,
    action,
}: {
    isLoading: boolean;
    show: boolean;
    action?(): void;
}): ActionLabeledInputButton => {
    return {
        background: 'transparent',
        iconName: isLoading ? 'ri-loader-4-line' : 'ri-arrow-down-s-line',
        iconClass: twMerge(' transition-all', show && 'rotate-180', isLoading && 'text-primary animate-spin'),
        action,
    };
};
